import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { dataHooks, ALIGN, PADDING } from './constants';
import { st, classes } from './TableListItem.st.css.js';
import Checkbox from '../Checkbox';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { DragHandle } from '../DragHandle/DragHandle';
import { fillRef } from '../utils/fillRef';
import { useDraggableContainer } from '../DraggableContainer/useDraggableContainer';
import { CaretRight } from '@wix/wix-ui-icons-common/system';
const getWidthStyle = (options) => options.reduce((acc, { width }) => `${acc} ${typeof width === 'number' ? width + 'px' : width || '1fr'}`, '');
/** TableListItem */
const TableListItem = forwardRef(({ options, verticalPadding = PADDING.small, horizontalPadding = PADDING.large, checkbox = false, checkboxDisabled, checkboxTooltipContent, checkboxTooltipProps, checked, onCheckboxChange = () => { }, draggable = false, focused, onBlur, dragDisabled, showDivider = false, onKeyUp, onClick, dragging, className, dataHook, dragHandleProps, border = false, dragHandleSize = 'large', expandable = false, expandDisabled, onClickExpand, expanded = false, }, ref) => {
    const draggableRef = useRef(null);
    const rootRef = useRef(null);
    React.useEffect(() => {
        if (draggableRef.current && focused) {
            draggableRef.current.focus();
        }
    }, [draggableRef, focused]);
    React.useImperativeHandle(ref, () => ({
        focus: () => rootRef.current && rootRef.current.focus(),
    }));
    const { className: draggableContainerClassName } = useDraggableContainer({
        dragging,
        draggable,
        dragDisabled,
        highlight: true,
    });
    const showControls = draggable || expandable || checkbox;
    return (React.createElement(WixDesignSystemContext.Consumer, null, ({ newColorsBranding }) => (React.createElement("div", { ref: rootRef, tabIndex: -1, onClick: onClick, className: st(classes.root, {
            draggable: draggable && !dragDisabled,
            dragDisabled,
            dragging,
            checked: checkbox && checked,
            showDivider,
            clickable: !!onClick,
            verticalPadding,
            horizontalPadding,
            newColorsBranding,
            border,
            dragHandleSize,
        }, className, draggableContainerClassName), "data-hook": dataHook },
        showControls && (React.createElement("div", { className: st(classes.controlsContainer, { dragHandleSize }) },
            draggable && (React.createElement("div", { className: classes.handleContainer },
                React.createElement(DragHandle, Object.assign({ tabIndex: onKeyUp ? 0 : undefined }, dragHandleProps, { domRef: el => {
                        draggableRef.current = el;
                        fillRef(dragHandleProps === null || dragHandleProps === void 0 ? void 0 : dragHandleProps.domRef, el);
                    }, onBlur: onBlur, onKeyUp: onKeyUp, dragging: dragging, disabled: dragDisabled, dataHook: dataHooks.tableListItemDragHandle, dragHandleSize: dragHandleSize })))),
            expandable && (React.createElement("div", { className: st(classes.expandHandle, {
                    expandable: expandable && !expandDisabled,
                }), "data-hook": dataHooks.tableListItemExpandHandleContainer },
                React.createElement(CaretRight, { className: st(classes.caretIcon, {
                        expanded,
                    }), onClick: expandDisabled ? undefined : onClickExpand, "data-hook": dataHooks.tableListItemExpandHandle }))),
            checkbox && (React.createElement("div", { className: st(classes.checkbox, { checkboxDisabled }), "data-hook": dataHooks.tableListItemCheckboxContainer },
                React.createElement(Checkbox, { checked: checked, disabled: checkboxDisabled, onChange: checkboxDisabled ? undefined : onCheckboxChange, dataHook: dataHooks.tableListItemCheckbox, tooltipContent: checkboxTooltipContent, tooltipProps: checkboxTooltipProps }))))),
        React.createElement("div", { className: classes.optionsContainer, style: {
                gridTemplateColumns: getWidthStyle(options),
            }, "data-hook": dataHooks.tableListItemOptionsContainer }, options.map(({ value, align }, index) => (React.createElement("div", { className: st(classes.align, {
                position: align && ALIGN[align],
            }), key: index, "data-hook": dataHooks.tableListItemValue }, value))))))));
});
TableListItem.displayName = 'TableListItem';
TableListItem.propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    // @ts-expect-error incompatibility https://github.com/DefinitelyTyped/DefinitelyTyped/issues/45094
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.node.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        align: PropTypes.oneOf([ALIGN.left, ALIGN.center, ALIGN.right]),
    }).isRequired).isRequired,
    verticalPadding: PropTypes.oneOf([
        PADDING.large,
        PADDING.medium,
        PADDING.small,
        PADDING.tiny,
        PADDING.xTiny,
        PADDING.xxTiny,
    ]),
    horizontalPadding: PropTypes.oneOf([
        PADDING.large,
        PADDING.medium,
        PADDING.small,
        PADDING.tiny,
        PADDING.xTiny,
        PADDING.xxTiny,
    ]),
    dragging: PropTypes.bool,
    checkbox: PropTypes.bool,
    checkboxDisabled: PropTypes.bool,
    checked: PropTypes.bool,
    onCheckboxChange: PropTypes.func,
    draggable: PropTypes.bool,
    dragDisabled: PropTypes.bool,
    showDivider: PropTypes.bool,
    onBlur: PropTypes.func,
    onKeyUp: PropTypes.func,
    focused: PropTypes.bool,
    onClick: PropTypes.func,
    dragHandleProps: PropTypes.object,
    checkboxTooltipContent: PropTypes.node,
    checkboxTooltipProps: PropTypes.object,
    border: PropTypes.bool,
    dragHandleSize: PropTypes.oneOf(['small', 'large']),
    expandable: PropTypes.bool,
    expanded: PropTypes.bool,
    onClickExpand: PropTypes.func,
    expandDisabled: PropTypes.bool,
};
export default TableListItem;
//# sourceMappingURL=TableListItem.js.map